import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { anonymizeVisits } from '../../utils/demoAnonymizer'
import { getAspirePropertyId, getPropertyCoordinates, buildGeoInsights, buildSidebarData, getDayAbbrev, isRowProblematic } from './propertyDetailHelpers'

// Self-fetching counterpart to the controlled props the table passes into the
// drawer. Mirrors RoutingPropertiesTable's visit-history / yard-route effects
// and derived block exactly, so self-fetch mode reproduces controlled mode.
export function usePropertyDetailController ({ property, url, allRows = [], demoMode = false, enabled = true }) {
  // UI state — defaults match RoutingPropertiesTable.js.
  const [activeTab, setActiveTab] = useState('overview')
  const [hoursView, setHoursView] = useState('visits')
  const [detailHistoryRange, setDetailHistoryRange] = useState('90')
  const [hoveredVisit, setHoveredVisit] = useState(null)
  const [pinnedVisit, setPinnedVisit] = useState(null)
  const [visitTypeFilter, setVisitTypeFilter] = useState('all')
  const [detailHistory, setDetailHistory] = useState({ loading: false, error: null, visits: [] })
  const [yardRoute, setYardRoute] = useState({ loading: false, error: null, data: null })

  // visit-history effect (mirror of RoutingPropertiesTable lines 1128–1169).
  useEffect(() => {
    const propertyId = getAspirePropertyId(property)
    if (!enabled || !propertyId || !url) {
      setDetailHistory({ loading: false, error: null, visits: [] })
      return
    }

    let cancelled = false
    const fetchDetailHistory = async () => {
      setDetailHistory(prev => ({ ...prev, loading: true, error: null }))
      try {
        const response = await axios.get(`${url}/api/aspire/routing/properties/${propertyId}/visit-history`, {
          params: { range: detailHistoryRange }
        })
        if (!cancelled) {
          let visits = response.data?.visits || []
          if (demoMode) {
            visits = anonymizeVisits(visits)
          }
          setDetailHistory({ loading: false, error: null, visits })
        }
      } catch (error) {
        console.error('Error fetching property detail history:', error)
        if (!cancelled) {
          setDetailHistory({ loading: false, error: error.message, visits: [] })
        }
      }
    }

    fetchDetailHistory()
    return () => {
      cancelled = true
    }
  }, [url, property, detailHistoryRange, demoMode, enabled])

  // yard-route effect (mirror of RoutingPropertiesTable lines 1171–1202).
  useEffect(() => {
    const propertyId = getAspirePropertyId(property)
    if (!enabled || !propertyId || !url || !getPropertyCoordinates(property)) {
      setYardRoute({ loading: false, error: null, data: null })
      return
    }

    let cancelled = false
    const fetchYardRoute = async () => {
      setYardRoute({ loading: true, error: null, data: null })
      try {
        const response = await axios.get(`${url}/api/aspire/routing/properties/${propertyId}/yard-route`)
        if (!cancelled) {
          setYardRoute({ loading: false, error: null, data: response.data || null })
        }
      } catch (error) {
        console.error('Error fetching yard route:', error)
        if (!cancelled) {
          setYardRoute({
            loading: false,
            error: error.response?.data?.message || error.response?.data?.error || error.message,
            data: null
          })
        }
      }
    }

    fetchYardRoute()
    return () => {
      cancelled = true
    }
  }, [url, property, enabled])

  // Derived values (mirror of RoutingPropertiesTable lines 1508–1529).
  const geoInsights = useMemo(() => ({
    ...buildGeoInsights(property, allRows),
    yardRoute: yardRoute.data,
    routeLoading: yardRoute.loading,
    routeError: yardRoute.error
  }), [property, allRows, yardRoute])

  const sidebarData = useMemo(
    () => buildSidebarData(property, detailHistory, geoInsights),
    [property, detailHistory, geoInsights]
  )

  const { trendPct, marginSpark, visitsSpark, dayAbbrev, dominantService } = useMemo(() => {
    const recentVisits = sidebarData.visitRows.filter(visit => visit.wAgo < 4)
    const priorVisits = sidebarData.visitRows.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
    const recentAvg = recentVisits.length
      ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
      : sidebarData.kpis.avgHours
    const priorAvg = priorVisits.length
      ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
      : recentAvg
    return {
      trendPct: priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0,
      marginSpark: sidebarData.monthlyRows.map(month => month.rev + month.othRev - month.cost),
      visitsSpark: sidebarData.monthlyRows.map(month => month.visits),
      dayAbbrev: getDayAbbrev(sidebarData.scheduledDay),
      dominantService: sidebarData.serviceRows.slice().sort((a, b) => b.hours - a.hours)[0]
    }
  }, [sidebarData])

  const isProblematic = property ? isRowProblematic(property) : false

  return {
    activeTab,
    setActiveTab,
    hoursView,
    setHoursView,
    detailHistoryRange,
    setDetailHistoryRange,
    hoveredVisit,
    setHoveredVisit,
    pinnedVisit,
    setPinnedVisit,
    visitTypeFilter,
    setVisitTypeFilter,
    detailHistory,
    geoInsights,
    sidebarData,
    trendPct,
    marginSpark,
    visitsSpark,
    dayAbbrev,
    dominantService,
    isProblematic
  }
}

export const usePropertyDetail = usePropertyDetailController
